<template>
  <div>
    <!-- Поиск по компонентам -->
    <div v-if="searchable && rows.length > 1" class="flex flex-wrap items-center gap-3 mb-3">
      <div class="relative flex-1 min-w-[220px] max-w-sm">
        <svg class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"></path>
        </svg>
        <input
          v-model="search"
          type="text"
          :placeholder="placeholder"
          class="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          v-if="search"
          @click="search = ''"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          title="Очистить"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="text-xs text-slate-500">
        <template v-if="search">Найдено {{ filteredRows.length }} из {{ rows.length }}</template>
        <template v-else>Всего {{ rows.length }}</template>
      </div>
    </div>

    <!-- Таблица компонентов: парные поля выводим в две строки — сверху основное, снизу уточняющее -->
    <div class="bg-white rounded-lg border border-slate-200 overflow-x-auto">
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide">
            <th class="px-4 py-2 text-left font-semibold text-slate-500 min-w-[260px]">
              <div>Наименование</div>
              <div class="font-normal text-slate-400">Артикул</div>
            </th>
            <th class="px-4 py-2 text-right font-semibold text-slate-500 whitespace-nowrap">Кол-во</th>
            <th class="px-4 py-2 text-left font-semibold text-slate-500 whitespace-nowrap">
              <div>Серийный №</div>
              <div class="font-normal text-slate-400">Накладная</div>
            </th>
            <th class="px-4 py-2 text-left font-semibold text-slate-500 whitespace-nowrap">
              <div>Партия</div>
              <div class="font-normal text-slate-400">Поставщик</div>
            </th>
            <th class="px-4 py-2 text-left font-semibold text-slate-500 whitespace-nowrap">
              <div>Дата произв.</div>
              <div class="font-normal text-slate-400">Срок годности</div>
            </th>
            <th class="px-4 py-2 text-left font-semibold text-slate-500 whitespace-nowrap">
              <div>На склад</div>
              <div class="font-normal text-slate-400">Ячейка</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filteredRows" :key="row.key"
              class="border-b border-slate-100 last:border-b-0 odd:bg-white even:bg-slate-50/60 hover:bg-blue-50/50 transition-colors">
            <!-- Наименование + артикул -->
            <td class="px-4 py-2.5 align-top">
              <div class="font-medium">
                <span :class="row.isDeleted ? 'line-through text-slate-400' : (row.name ? 'text-slate-800' : 'text-slate-300')">
                  {{ row.name || '—' }}
                </span>
                <span v-if="row.isDeleted" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
              </div>
              <div class="text-xs font-light mt-0.5" :class="row.sku ? 'text-slate-400' : 'text-slate-300'">
                {{ row.sku || '—' }}
              </div>
            </td>

            <!-- Количество -->
            <td class="px-4 py-2.5 align-top text-right tabular-nums whitespace-nowrap"
                :class="row.count ? 'text-slate-600' : 'text-slate-300'">
              {{ row.count || '—' }}
            </td>

            <!-- Серийный номер + накладная -->
            <td class="px-4 py-2.5 align-top whitespace-nowrap">
              <div :class="row.serialNumber ? 'text-slate-600' : 'text-slate-300'">{{ row.serialNumber || '—' }}</div>
              <div class="text-xs font-light mt-0.5" :class="row.invoice ? 'text-slate-400' : 'text-slate-300'">
                {{ row.invoice || '—' }}
              </div>
            </td>

            <!-- Партия + поставщик -->
            <td class="px-4 py-2.5 align-top">
              <div class="whitespace-nowrap" :class="row.batch ? 'text-slate-600' : 'text-slate-300'">{{ row.batch || '—' }}</div>
              <div class="text-xs font-light mt-0.5" :class="row.supplier ? 'text-slate-400' : 'text-slate-300'">
                {{ row.supplier || '—' }}
              </div>
            </td>

            <!-- Дата производства + срок годности -->
            <td class="px-4 py-2.5 align-top whitespace-nowrap tabular-nums">
              <div :class="row.manufactureDate ? 'text-slate-600' : 'text-slate-300'">{{ row.manufactureDate || '—' }}</div>
              <div class="text-xs font-light mt-0.5" :class="row.expiryDate ? 'text-slate-400' : 'text-slate-300'">
                {{ row.expiryDate || '—' }}
              </div>
            </td>

            <!-- Поступление на склад + ячейка -->
            <td class="px-4 py-2.5 align-top">
              <div class="whitespace-nowrap tabular-nums" :class="row.receiptDate ? 'text-slate-600' : 'text-slate-300'">
                {{ row.receiptDate || '—' }}
              </div>
              <div class="text-xs font-light mt-0.5" :class="row.cell ? 'text-slate-400' : 'text-slate-300'">
                {{ row.cell || '—' }}
              </div>
            </td>
          </tr>
          <tr v-if="filteredRows.length === 0">
            <td colspan="6" class="px-4 py-5 text-center text-sm text-slate-500 italic">
              {{ rows.length === 0 ? 'Компоненты не указаны' : 'Ничего не найдено' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment/moment.js';

// Поля, по которым работает поиск (порядок не важен — они просто склеиваются в строку)
const SEARCHABLE_FIELDS = [
  'name', 'sku', 'count', 'serialNumber', 'invoice',
  'batch', 'supplier', 'manufactureDate', 'expiryDate', 'receiptDate', 'cell',
];

export default {
  name: 'TaskProductsTable',
  props: {
    // Список task_product (либо «заглушек» из компонентов шага без привязки к складу)
    products: {
      type: Array,
      default: () => [],
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Поиск по компонентам',
    },
  },
  data() {
    return {
      search: '',
    };
  },
  computed: {
    // Плоское представление: и для вывода, и для поиска — одна и та же строка.
    // Отсутствующие данные оставляем пустыми (null), прочерк подставляет шаблон
    rows() {
      return this.products.map((product, index) => {
        const storehouseProduct = product.storehouse_product || null;
        const batchProduct = storehouseProduct?.batch_product || null;
        const receiptInvoice = batchProduct?.receipt_invoice_product?.receipt_invoice || null;
        const batchManufacture = batchProduct?.batch_manufacture || null;
        const productData = batchProduct?.product || product.product || null;

        return {
          key: product.id ? 'tp-' + product.id + '-' + index : 'comp-' + index,
          name: productData?.name || null,
          sku: productData?.sku || null,
          isDeleted: Boolean(productData?.deleted_at),
          count: batchProduct ? '1' : this.formatCount(product.count),
          serialNumber: batchProduct ? (batchProduct.serial_number || null) : null,
          invoice: receiptInvoice?.number
            ? receiptInvoice.number + ' (' + receiptInvoice.id + ')'
            : null,
          batch: batchManufacture?.number || null,
          supplier: batchManufacture?.supplier?.name || receiptInvoice?.supplier?.name || null,
          manufactureDate: this.formatDate(batchManufacture?.manufacture_date),
          expiryDate: this.formatDate(batchManufacture?.expiry_date),
          receiptDate: this.formatDate(storehouseProduct?.created_at),
          cell: storehouseProduct?.storehouse_cell?.structure_item?.full_path || null,
        };
      });
    },
    filteredRows() {
      const query = this.search.trim().toLowerCase();
      if (!query) return this.rows;

      return this.rows.filter(row => SEARCHABLE_FIELDS
        .map(field => row[field] || '')
        .join(' ')
        .toLowerCase()
        .includes(query));
    },
  },
  methods: {
    formatCount(value) {
      if (value == null) return null;
      const num = Number(value);
      return isNaN(num) ? String(value) : (Math.round(num * 1000) / 1000).toString();
    },
    formatDate(date) {
      if (!date) return null;
      const momentDate = moment(date);
      return momentDate.isValid() ? momentDate.local().format('DD.MM.YYYY') : null;
    },
  },
};
</script>
